import { MoreVert } from "@mui/icons-material";
import { IconButton, Link, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import { UrlPathReceiptScanningDetail } from "../../api/url";
import { canMaintainBonus, canMaintainReceipts, getCurrentUsername } from "../../store/actions/LoginActions";
import { IStore } from "../../store/IStore";
import { PageViewMode } from "../../store/models/PageViewMode";
import { useTranslation } from "react-i18next";
import { ReceiptDto } from "../../store/models/receipt/ReceiptDto";
import { thunkCreateErrorNotification, thunkCreateSuccessNotification } from "../../store/actions/NotificationActions";
import { Api } from "../../api/Api";
import KnistrConfirmDialog from "../atoms/KnistrConfirmDialog";

const canEdit = (receipt: ReceiptDto) => {
  if (!canMaintainBonus()) return false;
  return true;
};

const canApprove = (receipt: ReceiptDto) => {
  if (!canMaintainReceipts()) return false;
  return receipt.status === "WAITING_FOR_APPROVAL";
};

const canReject = (receipt: ReceiptDto) => {
  if (!canMaintainReceipts()) return false;
  return receipt.status === "WAITING_FOR_APPROVAL";
};

const shouldShowMenu = (receipt: ReceiptDto) => {
  // Hide menu completely for submitted receipts (UPLOADING and UPLOADED statuses)
  if (receipt.status === "UPLOADING" || receipt.status === "UPLOADED") return false;
  return true;
};

const isFinalState = (status: string) => {
  // Final states where only Edit should be available
  return ["COMPLETED", "REJECTED", "APPROVED"].includes(status);
};

interface ReceiptMenuProps {
  receipt: ReceiptDto;
  onReceiptUpdated?: (receipt: ReceiptDto) => void;
}

const ReceiptMenu = (props: ReceiptMenuProps & ThunkProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [confirmRejectOpen, setConfirmRejectOpen] = useState<boolean>(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(undefined);
    event.stopPropagation();
  };
  const canApproveReceipt = canApprove(props.receipt);
  const canRejectReceipt = canReject(props.receipt);
  const canEditReceipt = canEdit(props.receipt);
  const showMenu = shouldShowMenu(props.receipt);
  const isInFinalState = isFinalState(props.receipt.status);
  const { t } = useTranslation();

  const approveReceipt = async (receipt: ReceiptDto) => {
    try {
      await Api.approveReceipt(receipt.receiptNumber);
      props.thunkCreateSuccessNotification(
        t("receiptOverview.approveSuccess", { receiptNumber: receipt.receiptNumber })
      );
    } catch (error) {
      props.thunkCreateErrorNotification(
        t("receiptOverview.approveFailure", { receiptNumber: receipt.receiptNumber }),
        error
      );
      return;
    } finally {
      if (props.onReceiptUpdated) {
        props.onReceiptUpdated({ ...receipt, status: "APPROVED", reviewedBy: getCurrentUsername() });
      }
    }
  };
  const rejectReceipt = async (receipt: ReceiptDto) => {
    try {
      await Api.rejectReceipt(receipt.receiptNumber);
      props.thunkCreateSuccessNotification(
        t("receiptOverview.rejectSuccess", { receiptNumber: receipt.receiptNumber })
      );
    } catch (error) {
      props.thunkCreateErrorNotification(
        t("receiptOverview.rejectFailure", { receiptNumber: receipt.receiptNumber }),
        error
      );
      return;
    } finally {
      if (props.onReceiptUpdated) {
        props.onReceiptUpdated({ ...receipt, status: "REJECTED", reviewedBy: getCurrentUsername() });
      }
    }
  };

  const handleRejectClick = (e: React.MouseEvent<HTMLElement>) => {
    setConfirmRejectOpen(true);
    handleClose(e);
  };
  const handleApproveClick = (e: React.MouseEvent<HTMLElement>) => {
    setConfirmApproveOpen(true);
    handleClose(e);
  };
  const handleRejectConfirm = async () => {
    await rejectReceipt(props.receipt);
  };
  const handleApproveConfirm = async () => {
    await approveReceipt(props.receipt);
  };

  if (!showMenu) {
    return null;
  }

  return (
    <div>
      {confirmRejectOpen && (
        <KnistrConfirmDialog
          title={t("receiptOverview.confirmRejectTitle")}
          description={t("receiptOverview.confirmRejectMessage", { receiptNumber: props.receipt.receiptNumber })}
          cancelButton={t("common.cancel")}
          confirmButton={t("receiptOverview.table.reject")}
          onConfirm={handleRejectConfirm}
          setConfirmVisible={setConfirmRejectOpen}
        />
      )}
      {confirmApproveOpen && (
        <KnistrConfirmDialog
          title={t("receiptOverview.confirmApproveTitle")}
          description={t("receiptOverview.confirmApproveMessage", { receiptNumber: props.receipt.receiptNumber })}
          cancelButton={t("common.cancel")}
          confirmButton={t("receiptOverview.table.approve")}
          onConfirm={handleApproveConfirm}
          setConfirmVisible={setConfirmApproveOpen}
        />
      )}
      <IconButton onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu 
        id="long-menu" 
        anchorEl={anchorEl} 
        keepMounted 
        open={open} 
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {!isInFinalState && canApproveReceipt && (
          <MenuItem onClick={handleApproveClick}>
            {t("receiptOverview.table.approve")}
          </MenuItem>
        )}
        {!isInFinalState && canRejectReceipt && (
          <MenuItem onClick={handleRejectClick}>
            {t("receiptOverview.table.reject")}
          </MenuItem>
        )}
        {canEditReceipt && (
          <MenuItem onClick={(e) => handleClose(e)}>
            <Link
              component={RouterLink}
              to={UrlPathReceiptScanningDetail.replace(":id", props.receipt.receiptNumber).replace(
                ":viewMode",
                PageViewMode.EDIT
              )}
            >
              {t("receiptOverview.table.edit")}
            </Link>
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

const mapStateToProps = (_state: IStore) => ({});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ thunkCreateErrorNotification, thunkCreateSuccessNotification }, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(ReceiptMenu);
